import { html } from "htm/preact";
import { useEffect, useState } from "preact/hooks";
import { navigate } from "../router.js";
import {
  ApiError,
  claimPlayer,
  getPublicRoster,
  spectate,
  type PublicRoster,
} from "../http.js";
import { getAuth, rememberTournament, setAuth } from "../storage.js";
import { Icon } from "../components/Icon.js";
import { anEvent } from "../copy.js";

export function Join({ code: initialCode }: { code: string }) {
  const [code, setCode] = useState(initialCode.toUpperCase());
  const [roster, setRoster] = useState<PublicRoster | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingClaim, setPendingClaim] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const auth = getAuth(code);
    // Already in? Go where that role belongs — a spectator following the invite link a
    // second time wants the screen they came for, not the standings.
    if (auth) navigate(auth.role === "spectator" ? `/t/${code}/clubhouse` : `/t/${code}/leaderboard`);
    else if (code.length === 4) loadRoster();
  }, [code]);

  async function loadRoster(e?: Event) {
    e?.preventDefault();
    setError(null);
    setLoading(true);
    try {
      setRoster(await getPublicRoster(code));
    } catch (err) {
      setError((err as Error).message);
      setRoster(null);
    } finally {
      setLoading(false);
    }
  }

  /** The other way in: no name taken, nothing claimed off anyone, read-only. */
  async function watch() {
    setError(null);
    try {
      const { token, name } = await spectate(code);
      setAuth(code, { token, role: "spectator", playerId: null });
      rememberTournament({ code, name: name || roster?.name || code, role: "spectator" });
      navigate(`/t/${code}/clubhouse`);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function claim(playerId: string, confirm: boolean) {
    setError(null);
    try {
      const { token } = await claimPlayer(code, playerId, confirm);
      setAuth(code, { token, role: "player", playerId });
      rememberTournament({ code, name: roster?.name ?? code, role: "player" });
      setPendingClaim(null);
      navigate(`/t/${code}/leaderboard`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setPendingClaim(playerId);
      else setError((err as Error).message);
    }
  }

  return html`
    <h1>${`Join ${anEvent()}`}</h1>
    <form class="stack" onSubmit=${loadRoster}>
      <label class="field">
        <span class="field-label">Code</span>
        <input
          class="code-input"
          value=${code}
          maxlength="4"
          placeholder="ABCD"
          autocapitalize="characters"
          autocomplete="off"
          spellcheck="false"
          onInput=${(e: any) => setCode(e.currentTarget.value.toUpperCase())}
        />
      </label>
      <button type="submit" class="secondary" disabled=${loading || code.length !== 4}>
        ${loading ? "Looking…" : "View roster"}
      </button>
    </form>

    ${error &&
    html`<p class="notice notice-error mt-3"><${Icon} name="alert" size=${16} /> ${error}</p>`}

    ${roster &&
    html`
      <h2>${roster.name}</h2>
      <p class="field-help mt-0">Tap your name to join. Your scores land on whoever you pick.</p>
      <div class="tap-list">
        ${roster.players.map(
          (p) => html`
            <button
              key=${p.id}
              class=${p.claimed ? "claimed" : ""}
              onClick=${() => (p.claimed ? setPendingClaim(p.id) : claim(p.id, false))}
            >
              <span class="avatar">${p.name.trim().charAt(0).toUpperCase()}</span>
              <span class="list-main">${p.name}</span>
              ${p.claimed
                ? html`<span class="badge">claimed</span>`
                : html`<${Icon} name="right" size=${18} />`}
            </button>
          `,
        )}
      </div>

      ${roster.spectatorsEnabled &&
      html`
        <div class="divider"></div>
        <button class="secondary" onClick=${watch}>
          <${Icon} name="eye" size=${18} /> Just here to watch
        </button>
        <p class="field-help">
          Follow along on the clubhouse screen without taking a name. You won't be able
          to enter scores.
        </p>
      `}
    `}

    ${pendingClaim &&
    html`
      <div class="card card-accent">
        <p>
          <strong>${roster?.players.find((p) => p.id === pendingClaim)?.name ?? "This name"}</strong>
          is already claimed on another phone. Reclaim it here? The other phone will stop
          syncing.
        </p>
        <div class="row">
          <button class="grow" onClick=${() => claim(pendingClaim, true)}>Reclaim</button>
          <button class="secondary" onClick=${() => setPendingClaim(null)}>Cancel</button>
        </div>
      </div>
    `}
  `;
}
